// A customer's routing rule saved (`routing-rule`, Admin → Routing rules and Admin → Customers'
// check): one record per code, each with its own System log entry (restorable by a manager). A rule
// set on a shown-name group is one save per code, one after another (the platform's CRUD has no
// batch). Reset empties the code's record — days, No rule and Separate cleared — so the fallback (the
// group's, A2000's) is back and the code joins its group again: a save like any other (a PUT, never a
// DELETE), restorable from the System log, and an empty record counts as none. Every write sends both
// flags (`noRule`, `separate`: NOT NULL in 019). No React: `routing-rule-writes.ts` is its hook, and
// `scripts/routing-checks.ts` checks that a failed save (any non-2xx) writes no entry and counts as failed.

import { ROUTING_RULES, routingRuleText, valuesOf } from "@app/shared";
import type { FieldChange, LineSource, RoutingRuleRecord, RuleValues } from "@app/shared";
import type { LoggedWrites, Rec } from "./logged-write-core";

/** A code a rule is saved on: its source, code, original name and record (none yet: created). */
export interface RuleTarget {
  source: LineSource;
  code: string;
  name: string;
  record: RoutingRuleRecord | null;
}

/** A day count as the log says it: "" for none; a flag "1" or "0". */
const said = (n: number | null | undefined) => (n === null || n === undefined ? "" : String(n));
const flag = (b: boolean) => (b ? "1" : "0");

/** What a save changes, field by field: only those that differ. */
function changesOf(record: RoutingRuleRecord | null, to: RuleValues, notes: string): FieldChange[] {
  const had = valuesOf(record);
  const from = {
    openBdays: said(had.openBdays),
    dueBdays: said(had.dueBdays),
    noRule: flag(had.noRule),
    separate: flag(had.separate),
    notes: String(record?.notes ?? ""),
  };
  const next = {
    openBdays: said(to.openBdays),
    dueBdays: said(to.dueBdays),
    noRule: flag(to.noRule),
    separate: flag(to.separate),
    notes,
  };
  return (["openBdays", "dueBdays", "noRule", "separate", "notes"] as const).flatMap((f) =>
    from[f] === next[f] ? [] : [{ f, from: from[f], to: next[f] }],
  );
}

/** A record that sets nothing: Reset's. It counts as no record (`routingRuleFor`, the Admin list). */
const EMPTY: RuleValues = { openBdays: null, dueBdays: null, noRule: false, separate: false };

/**
 * What a line's boxes hold after its save (`outcome`: the rows that failed, null when the person
 * cancelled): emptied once saved; the typed value kept when it failed or was cancelled, to try again.
 */
export const typedAfterSave = <T>(typed: T | null, outcome: ReadonlySet<string> | null): T | null =>
  outcome === null || outcome.size > 0 ? typed : null;

/** What the writes need: the logged writes, a record's version now, who saves, and an error's words. */
export interface RuleWriterDeps {
  writes: LoggedWrites;
  get: (entity: string, id: string) => Promise<Rec>;
  who: string;
  errorText: (e: unknown) => string;
}

export function ruleWriter({ writes, get, who, errorText }: RuleWriterDeps) {
  /** One code's record: its days and flags. Nothing to change: no save. */
  const save = async (t: RuleTarget, values: RuleValues, notes: string, note: string) => {
    const changes = changesOf(t.record, values, notes);
    if (t.record && !changes.length) return;
    const log = { action: "edit" as const, lineText: routingRuleText(t), changes, note };
    const input = {
      source: t.source,
      code: t.code,
      customerName: t.name,
      openBdays: values.openBdays,
      dueBdays: values.dueBdays,
      noRule: values.noRule ? 1 : 0,
      separate: values.separate ? 1 : 0,
      notes,
      updatedBy: who,
    };
    if (t.record) {
      // Its version now: another manager may have saved it since.
      const now = await get(ROUTING_RULES.id, t.record.id);
      await writes.update(ROUTING_RULES, t.record.id, String(now.rv), input, log);
    } else await writes.create(ROUTING_RULES, input, log);
  };

  /** One code back to the fallback, in its group again: its record emptied. None: nothing to do. */
  const reset = async (t: RuleTarget, note: string) => {
    if (!t.record) return;
    await save(t, EMPTY, "", note);
  };

  /** Each target one after another; the failed ones with why. */
  const each = async (targets: readonly RuleTarget[], run: (t: RuleTarget) => Promise<void>) => {
    const failed: { target: RuleTarget; why: string }[] = [];
    let saved = 0;
    for (const t of targets) {
      try {
        await run(t);
        saved++;
      } catch (e) {
        failed.push({ target: t, why: errorText(e) });
      }
    }
    return { saved, failed };
  };

  return { save, reset, each };
}
